import { RedirectToLoginButton } from "@authentication/components/RedirectToLoginButton";
import { authenticationService } from "@authentication/services/AuthenticationService";
import { Media } from "@components/Media";
import { invenioConfig } from "@config";
import {
  AuthenticationRoutes,
  BackOfficeRoutes,
  FrontSiteRoutes,
} from "@routes/urls";
import PropTypes from "prop-types";
import React, { Component } from "react";
import Overridable from "react-overridable";
import { Link } from "react-router-dom";
import {
  Container,
  Dropdown,
  Image,
  Menu,
  Header,
  Label,
} from "semantic-ui-react";
import { clearAllFiltersFromSession } from "@utils/SessionStorage";
import { AuditService } from "@api/audit/audit";
import { attendanceApi } from "@api/attendance/attendance";
import { sessionManager } from "@authentication/services/SessionManager";

class ILSMenu extends Component {
  state = {
    isActiveUser: false,
    loadingActiveStatus: true,
  };

  componentDidMount() {
    const { user } = this.props;
    if (user?.fullName) {
      this.loadActiveStatus(user.fullName);
    }
  }

  componentDidUpdate(prevProps) {
    const { user } = this.props;
    if (user?.fullName && prevProps.user?.fullName !== user.fullName) {
      this.loadActiveStatus(user.fullName);
    }
  }

  loadActiveStatus = async (fullName) => {
    try {
      const response = await attendanceApi.debugActiveSessions();
      const activeSessions = response?.data?.data || [];

      const normalize = (str) => str?.toLowerCase().replace(/\s+/g, " ").trim();

      const isActive = activeSessions.some(
        (session) => normalize(session.userName) === normalize(fullName)
      );

      this.setState({ isActiveUser: isActive, loadingActiveStatus: false });
    } catch (error) {
      this.setState({ loadingActiveStatus: false });
    }
  };

  logout = async () => {
    const { logout, sendErrorNotification, user } = this.props;

    try {
      AuditService.api
        .createLog({
          action: "Logout",
          details: `${user.fullName} has been logged out`,
          created_by: user.fullName,
        })
        .catch((error) => {
          // Don't throw - let logout continue
        });

      clearAllFiltersFromSession();

      await logout();

      window.location.href = FrontSiteRoutes.home;
    } catch (e) {
      sendErrorNotification(
        "Logout",
        "Something went wrong. Please try to logout again."
      );
    }
  };

  renderRightDropDown = (userMenuText) => {
    const { user } = this.props;

    const dropdownEntries = (
      <Dropdown.Menu>
        <Dropdown.Item as={Link} to={AuthenticationRoutes.profile}>
          My Profile
        </Dropdown.Item>
        {authenticationService.hasRoles(user, ["admin", "librarian"]) ? (
          <>
            <Dropdown.Divider />
            <Dropdown.Item as={Link} to={BackOfficeRoutes.home}>
              Backoffice
            </Dropdown.Item>
          </>
        ) : null}
        <Dropdown.Divider />
        <Dropdown.Item as={Link} to={FrontSiteRoutes.dashboard}>
          Dashboard
        </Dropdown.Item>
        <Dropdown.Divider />
        <Dropdown.Item onClick={this.logout}>Sign out</Dropdown.Item>
      </Dropdown.Menu>
    );

    return (
      <>
        <Media greaterThanOrEqual="computer">
          <Dropdown item text={userMenuText} icon="caret down">
            {dropdownEntries}
          </Dropdown>
        </Media>
        <Media lessThan="computer">
          <Dropdown item text={userMenuText} icon="bars">
            {dropdownEntries}
          </Dropdown>
        </Media>
      </>
    );
  };

  renderRightMenuItem = (userMenuText = "") => {
    const { isAnonymous } = this.props;
    const { isActiveUser, loadingActiveStatus } = this.state;

    if (isAnonymous) {
      return (
        <RedirectToLoginButton
          renderClass={Menu.Item}
          className="ils-menu-login-button"
          icon="sign in"
          content="Sign in"
        />
      );
    }

    // Get roles from sessionManager
    const userRoles = sessionManager.user?.roles || [];

    const isAdminOrLibrarian =
      userRoles.includes("admin") || userRoles.includes("librarian");

    return (
      <Menu.Item>
        <div style={{ display: "flex", alignItems: "center" }}>
          {this.renderRightDropDown(userMenuText)}
          {!loadingActiveStatus && !isAdminOrLibrarian && (
            <Label
              circular
              color={isActiveUser ? "yellow" : "red"}
              empty
              title={
                isActiveUser
                  ? "You are currently checked in"
                  : "You are currently checked out"
              }
              style={{ marginLeft: "0.5em" }}
            />
          )}
        </div>
      </Menu.Item>
    );
  };

  render() {
    const { user } = this.props;

    return (
      <Overridable id="ILSMenu.layout" {...this.props}>
        <>
          <Media greaterThanOrEqual="computer">
            <Menu
              stackable
              borderless
              inverted
              fixed="top"
              className="header-menu"
            >
              <Container className="container-ilsmenu">
                <Menu.Item header>
                  <div style={{ display: "flex" }}>
                    <Overridable id="ILSMenu.Logo">
                      {invenioConfig.APP.LOGO_SRC && (
                        <Link to="/">
                          <Image
                            src={invenioConfig.APP.LOGO_SRC}
                            size="tiny"
                            alt="Logo"
                            style={{ marginRight: "2em" }}
                          />
                        </Link>
                      )}
                    </Overridable>
                  </div>
                  <div>
                    <Header as="h2" textAlign="left" style={{ color: "white" }}>
                      <Header.Subheader className="fs-headline-section">
                        Romblon State University
                      </Header.Subheader>
                      <Header.Content>Learning Resource Center</Header.Content>
                    </Header>
                  </div>
                </Menu.Item>
                <Menu.Menu position="right">
                  <Overridable id="ILSMenu.RightMenuItems" />
                  <Menu.Item data-tour="ils-menu-profile">
                    {this.renderRightMenuItem(user.fullName)}
                  </Menu.Item>
                </Menu.Menu>
              </Container>
            </Menu>
          </Media>
          <Media lessThan="computer">
            <Menu
              borderless
              inverted
              fixed="top"
              className="mobile-header-menu"
              style={{ padding: "0.3em 0.5em", width: "100%" }} // full width
            >
              <Container
                fluid // make container take 100% width
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                {/* Logo */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    flexShrink: 0,
                  }}
                >
                  <Overridable id="ILSMenu.LogoMobile">
                    {invenioConfig.APP.LOGO_SRC && (
                      <Link to="/">
                        <Image
                          src={invenioConfig.APP.LOGO_SRC}
                          size="tiny"
                          alt="Logo"
                          style={{ marginRight: "1em" }}
                        />
                      </Link>
                    )}
                  </Overridable>
                </div>

                {/* Title */}
                <div style={{ marginLeft: "0.5em", flex: 1 }}>
                  <Header
                    as="h4"
                    textAlign="left"
                    style={{
                      color: "white",
                      margin: 0,
                      lineHeight: "1.1em",
                    }}
                  >
                    <Header.Subheader
                      className="fs-headline-section"
                      style={{ fontSize: "0.9em" }}
                    >
                      Romblon State University
                    </Header.Subheader>
                    <Header.Content style={{ fontSize: "1.1em" }}>
                      Learning Resource Center
                    </Header.Content>
                  </Header>
                </div>

                {/* Right Menu */}
                <Menu.Menu position="right" style={{ flexShrink: 0 }}>
                  <Overridable id="ILSMenu.RightMenuItemsMobile" />
                  {this.renderRightMenuItem()}
                </Menu.Menu>
              </Container>
            </Menu>
          </Media>
        </>
      </Overridable>
    );
  }
}

ILSMenu.propTypes = {
  user: PropTypes.object.isRequired,
  isAnonymous: PropTypes.bool.isRequired,
  logout: PropTypes.func.isRequired,
  sendErrorNotification: PropTypes.func.isRequired,
};

export default Overridable.component("ILSMenu", ILSMenu);
